import { FC, useCallback, useEffect, useMemo } from 'react';
import { AuthProvider, useAuth, UserManager } from 'oidc-react';
import { APP_ENV_CONFIG, USER_MANAGER_CONFIG } from './config';
import { LoginComplete } from './App/LoginComplete';
import styled, { createGlobalStyle } from 'styled-components';
import { Interview } from './App/Interview';
import { TabItem, Tabs } from './Components/Tabs';
import { USER_MANAGEMENT_ASSET_NAMES } from '../iac/constants';
import { SYM } from './Components/MaterialSymbol';
import { PurposePreface } from './App/Interview/PurposePreface';
import { ApplicationStateProvider, getEasyLayout, Route, useRouteContext } from '@resistdesign/voltra/app/utils';
import { Login } from './App/Login';
import { Documents } from './App/Documents';
import { Profile } from './App/Profile';
import { Admin } from './App/Admin';
import { AudioRecordingScreen } from './App/Interview/AudioRecordingScreen';
import { InterviewComplete } from './App/Interview/InterviewComplete';

const {
  oauth: {
    clientDomain: OAUTH_DOMAIN,
    clientId: OAUTH_CLIENT_ID,
    redirectSignIn: OAUTH_REDIRECT_SIGN_IN,
    scope: OAUTH_SCOPE,
  },
} = APP_ENV_CONFIG;

const { post_logout_redirect_uri: SIGNOUT_URL } = USER_MANAGER_CONFIG;
const USER_MANAGER = new UserManager(USER_MANAGER_CONFIG as any);
const HEADER_TAB_ITEMS: TabItem[] = [
  {
    label: 'Interview',
    onClick: () => history.pushState({}, '', '/interview'),
  },
  {
    label: 'Admin',
    onClick: () => history.pushState({}, '', '/admin'),
  },
];

const GlobalStyle = createGlobalStyle`
  html,
  body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
`;
const LayoutBase = styled.div`
  width: 100vw;
  height: 100vh;
  padding: 1em;
  gap: 1em;
`;
const {
  layout: Layout,
  areas: { Header, Main },
} = getEasyLayout(
  LayoutBase,
  styled.div`
    overflow: hidden;
  `
)`
  header, 1fr
  main, 12fr
  \\ 1fr
`;
const {
  layout: HeaderControls,
  areas: { Title, Controls, User },
} = getEasyLayout(
  styled(Header)`
    gap: 1em;
  `,
  styled.div`
    flex: 1 0 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: stretch;
  `
)`
  title controls user, 1fr
  \\ 10fr 2fr 1fr
`;
const UserControls = styled(User)`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1em;
`;

const InnerApp: FC = () => {
  const { userData, signOut, signIn, isLoading: authIsLoading = false } = useAuth();
  const goToDocumentAcceptance = useCallback(() => {
    history.pushState({}, '', '/sign-up');
  }, []);
  const onCancelAcceptance = useCallback(() => {
    history.pushState({}, '', '/');
  }, []);
  const signUp = useCallback(async () => {
    const signUpUrl = `https://${OAUTH_DOMAIN}/signup?client_id=${encodeURIComponent(
      OAUTH_CLIENT_ID
    )}&redirect_uri=${encodeURIComponent(OAUTH_REDIRECT_SIGN_IN)}&response_type=code&scope=${encodeURIComponent(
      OAUTH_SCOPE
    )}`;

    window.location.href = signUpUrl;
  }, []);
  const userGroups = useMemo<string[]>(() => {
    const { profile = {} } = userData || {};
    const { ['cognito:groups']: groups = [] } = profile as any;

    return groups;
  }, [userData]);
  const isAdmin = useMemo(() => userGroups.includes(USER_MANAGEMENT_ASSET_NAMES.GROUPS.ADMIN), [userGroups]);
  const { access_token: authorization = '' } = userData || {};
  const { currentWindowPath = '' } = useRouteContext();
  const isAdminPath = currentWindowPath.startsWith('/admin');
  const onLogout = useCallback(async () => {
    await signOut();
    window.location.href = SIGNOUT_URL as string;
  }, [signOut]);

  useEffect(() => {
    if (!authIsLoading && !authorization) {
      history.pushState({}, '', '/');
    }
  }, [authIsLoading, authorization]);

  return (
    <Layout>
      <HeaderControls>
        <Title>{authorization ? <h2>{isAdminPath ? 'Admin' : <>Interview&nbsp;</>}</h2> : <h2>Login</h2>}</Title>
        <Controls>{isAdmin ? <Tabs tabItems={HEADER_TAB_ITEMS} /> : undefined}</Controls>
        <UserControls>
          {authorization ? (
            <SYM title="Logout" style={{ cursor: 'pointer' }} onClick={onLogout}>
              logout
            </SYM>
          ) : undefined}
        </UserControls>
      </HeaderControls>
      <Main data-id="MAIN">
        <Route exact>
          <Login onSignIn={signIn} goToDocumentAcceptance={goToDocumentAcceptance} />
        </Route>
        <Route path="/sign-up">
          <Documents onAccept={signUp} onCancel={onCancelAcceptance} />
        </Route>
        <Route path="/login-complete">
          <LoginComplete authorization={authorization} />
        </Route>
        <Route path="/profile">
          <Profile authorization={authorization} />
        </Route>
        <Route path="/interview">
          {authorization ? (
            <>
              <Route exact>
                <PurposePreface authorization={authorization} />
              </Route>
              <Route path="/start">
                <Interview authorization={authorization} />
              </Route>
              <Route path="/audio">
                <AudioRecordingScreen authorization={authorization} />
              </Route>
              <Route path="/complete">
                <InterviewComplete />
              </Route>
            </>
          ) : undefined}
        </Route>
        <Route path="/admin">
          <Admin authorization={authorization} />
        </Route>
      </Main>
    </Layout>
  );
};

export const App: FC = () => {
  return (
    <ApplicationStateProvider>
      <AuthProvider userManager={USER_MANAGER} autoSignIn={false}>
        <GlobalStyle />
        <Route>
          <InnerApp />
        </Route>
      </AuthProvider>
    </ApplicationStateProvider>
  );
};
